import { createApiClient } from './apiClient';

const createTranslationsService = (language) => {
  const apiClient = createApiClient(language);
  
  return {
    /**
     * Get all available languages
     * @returns {Promise} Promise with response containing languages array
     */
    getLanguages: () => {
      return apiClient.get('/api/translations/languages/');
    },
    
    /**
     * Get translations for a specific language
     * @param {string} languageCode - The language code (e.g., 'en', 'ar', 'fr')
     * @returns {Promise} Promise with response containing translations
     */
    getTranslations: (languageCode) => {
      return apiClient.get(`/api/translations/${languageCode}/`);
    },

    /**
     * Update translations for a specific language
     * @param {string} languageCode - The language code (e.g., 'en', 'ar', 'fr')
     * @param {Object} translations - Translation key-value pairs
     * @returns {Promise} Promise with response
     */
    updateTranslations: (languageCode, translations) => {
      return apiClient.put(`/api/translations/${languageCode}/`, translations);
    },

    /**
     * Get all translation types
     * @returns {Promise} Promise with response containing translation types array
     */
    getTypes: () => {
      return apiClient.get('/api/translations/types/');
    },

    /**
     * Get a quote for translation order
     * @param {FormData} formData - Form data containing file and translation details
     * @returns {Promise} Promise with response containing quote details
     */
    getQuote: (formData) => {
      return apiClient.post('/api/translations/orders/get_quote/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        timeout: 300000, // 5 minute timeout specifically for file uploads
        onUploadProgress: progressEvent => {
          // You can use this to track upload progress if needed
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          console.log(`Upload Progress: ${percentCompleted}%`);
        }
      });
    },
    
    /**
     * Place an order for translation
     * @param {Object} orderData - Order data containing quote_id, user details, etc.
     * @returns {Promise} Promise with response containing order details and payment_link
     */
    placeOrder: (orderData) => {
      return apiClient.post('/api/translations/orders/place_order/', orderData, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        }
      });
    },

    /**
     * Get details for a specific order
     * @param {string} orderId - The ID of the order to retrieve
     * @returns {Promise} Promise with response containing order details
     */
    getOrderDetails: (orderId) => {
      return apiClient.get(`/api/translations/orders/get_order_details/?order_id=${orderId}`);
    },

    /**
     * Verify payment status after Thawani redirect.
     * Calls Thawani's Retrieve Session API and syncs our records.
     * @param {string} orderId - The translation order ID from the success URL
     * @returns {Promise} Promise with { payment_status, session_status, order_id, order_status }
     */
    verifyPayment: (orderId) => {
      return apiClient.get(`/api/payments/verify/?order_id=${orderId}`);
    }
  };
};

// Export the function instead of an instance
export default createTranslationsService; 